import { DialogComponent } from '@theme/dialog';
import { queryStoreDetail } from '@theme/query-store-detail';
import { decodeHtmlEntities } from '@theme/utilities';

/**
 * <store-walk-in-drawer> — singleton right slide-in drawer showing a walk-in
 * partner store's details (name, description, and address(es)).
 *
 * Shell markup: snippets/walk-in-drawer.liquid — reuses the same drawer chrome
 * as the mini-cart / subscription / Special Conditions drawers (no new drawer
 * component). Opening is event-driven, mirroring store-conditions-drawer.js:
 * any surface dispatches `theme:store-walk-in:open` on `document` with
 * `detail: { storeName, description, address }` — the store cards
 * (assets/ng-store-card.js) do this from their Walk In trigger.
 *
 * The native <dialog> (via DialogComponent.showDialog) provides the modal
 * focus trap, Esc/outside-click dismissal, and restores focus to the trigger
 * on close.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} storeName
 * @property {HTMLElement} description
 * @property {HTMLElement} descriptionRow
 * @property {HTMLElement} locations
 * @property {HTMLElement} locationsRow
 * @property {HTMLButtonElement} closeButton
 *
 * @extends {DialogComponent}
 */

/** Copy / copied glyphs for the per-address copy control (no external deps). */
const COPY_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>';
const CHECK_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

class StoreWalkInDrawer extends DialogComponent {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #copyTimer;

  /** Incremented on each open so a slow volumeId fetch can't overwrite a newer open. */
  #openToken = 0;

  /** Typed view of the base component's refs (same casting approach as store-conditions-drawer.js). */
  get #els() {
    return /** @type {any} */ (this.refs);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('theme:store-walk-in:open', this.#onOpenEvent);
    // Delegated: the address list is re-rendered on each open, so listen on the host.
    this.addEventListener('click', this.#onCopyClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('theme:store-walk-in:open', this.#onOpenEvent);
    this.removeEventListener('click', this.#onCopyClick);
    clearTimeout(this.#copyTimer);
  }

  /** @param {Event} event */
  #onOpenEvent = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    if (!detail) return;

    // Two feed modes:
    //  • inline data — SLP / A-Z directory cards already carry description/address.
    //  • volumeId only — partner-stores cards; fetch the store's details on open.
    const hasInlineData = detail.description != null || detail.address != null;

    if (hasInlineData || !detail.volumeId) {
      this.#openToken++;
      this.#renderStore(detail.storeName, detail.description, detail.address);
      this.showDialog();
      return;
    }

    const token = ++this.#openToken;
    this.#renderStore(detail.storeName || '', '', null);
    this.#showContentLoading();
    this.showDialog();
    this.#fetchByVolumeId(detail.volumeId, token);
  };

  /**
   * Renders the store name, description and address(es) into the drawer.
   *
   * @param {string} [storeName]
   * @param {unknown} [description]
   * @param {object | object[] | null} [address]
   */
  #renderStore(storeName, description, address) {
    const { storeName: nameEl, description: descEl, descriptionRow, locations, locationsRow } = this.#els;

    if (nameEl) nameEl.textContent = storeName || '';

    const descHtml = this.#sanitizeDescription(description);
    if (descEl) descEl.innerHTML = descHtml;
    if (descriptionRow) descriptionRow.hidden = descHtml.trim() === '';

    const addressesHtml = this.#formatAddresses(address);
    if (locations) locations.innerHTML = addressesHtml;
    if (locationsRow) locationsRow.hidden = addressesHtml === '';
  }

  /** Skeleton placeholders shown while store details are fetched by volumeId. */
  #showContentLoading() {
    const { description, descriptionRow, locations, locationsRow } = this.#els;
    const bar = (w) =>
      `<span class="tw:block tw:h-3.5 ${w} tw:animate-pulse tw:rounded tw:bg-[#f4f4f4] tw:motion-reduce:animate-none"></span>`;
    if (description)
      description.innerHTML = `<span class="tw:flex tw:flex-col tw:gap-2">${bar('tw:w-full')}${bar('tw:w-3/4')}</span>`;
    if (descriptionRow) descriptionRow.hidden = false;
    if (locations)
      locations.innerHTML = `<span class="tw:flex tw:flex-col tw:gap-2">${bar('tw:w-2/3')}${bar('tw:w-1/2')}</span>`;
    if (locationsRow) locationsRow.hidden = false;
  }

  /**
   * Fetches a store's details by volumeId (store-detail API) and renders them.
   * Guarded by `#openToken` so a stale response can't overwrite a newer open;
   * degrades gracefully (empty sections hide) on failure.
   *
   * @param {string} volumeId
   * @param {number} token
   */
  async #fetchByVolumeId(volumeId, token) {
    let store = null;
    try {
      const result = await queryStoreDetail({
        volumeIds: [String(volumeId)],
        fields: ['volumeId', 'name', 'catalogName', 'derived', 'address', 'description'],
      });
      store = result?.ok ? result.data?.store : null;
    } catch {
      store = null;
    }

    if (token !== this.#openToken) return; // a newer open superseded this fetch

    if (!store) {
      this.#renderStore(this.#els.storeName?.textContent || '', '', null);
      return;
    }

    const name =
      decodeHtmlEntities(store.derived?.title || store.name || store.catalogName || '') ||
      this.#els.storeName?.textContent ||
      '';
    this.#renderStore(name, store.description, store.address);
  }

  /**
   * Normalises the address payload (a single object today, tolerant of an
   * array if the API ever returns multiple locations) into one divided list
   * item per address. Returns '' when there is nothing to show.
   *
   * @param {object | object[] | null | undefined} address
   * @returns {string}
   */
  #formatAddresses(address) {
    const list = Array.isArray(address) ? address : address ? [address] : [];
    const blocks = [];
    for (const addr of list) {
      // `blocks.length === 0` marks the first *rendered* location, so a leading
      // empty address object doesn't leave the first real item with a divider.
      const html = this.#renderLocation(addr, blocks.length === 0);
      if (html) blocks.push(html);
    }
    return blocks.join('');
  }

  /**
   * One location list item: the address lines plus a copy control. Each item
   * carries a bottom divider so single and multiple locations both read as a
   * clearly separated list.
   *
   * @param {Record<string, unknown>} addr
   * @returns {string}
   */
  #renderLocation(addr, isFirst) {
    const lines = this.#formatAddress(addr);
    if (!lines) return '';

    const plain = this.#plainAddress(addr);
    const copyBtn = plain
      ? `<button type="button" data-copy-address="${this.#escapeAttr(plain)}" aria-label="Copy address" class="tw:mt-0.5 tw:inline-flex tw:size-5 tw:shrink-0 tw:cursor-pointer tw:items-center tw:justify-center tw:border-0 tw:bg-transparent tw:p-0 tw:text-coal tw:transition-opacity tw:hover:opacity-70">${COPY_ICON}</button>`
      : '';

    // Divider sits BETWEEN locations — a top border on every item except the
    // first, with balanced padding — so multiple addresses read as one cohesive
    // list and a lone address has no orphan line.
    const layout = isFirst ? 'tw:pb-3' : 'tw:border-t tw:border-[#E6E6E6] tw:py-3';

    return `<div class="tw:flex tw:items-start tw:justify-between tw:gap-3 ${layout}">
        <p class="tw:m-0 tw:text-[14px] tw:leading-5 tw:text-coal">${lines}</p>
        ${copyBtn}
      </div>`;
  }

  /**
   * Joins the populated parts of one address object into escaped display lines.
   *
   * @param {Record<string, unknown>} addr
   * @returns {string} HTML (escaped text + <br>), or '' if the object is empty.
   */
  #formatAddress(addr) {
    if (!addr || typeof addr !== 'object') return '';

    const cityLine = [addr.city, addr.state, addr.zip]
      .map((p) => (typeof p === 'string' ? p.trim() : ''))
      .filter(Boolean)
      .join(', ');

    return [addr.addressLine1, addr.addressLine2, addr.addressLine3, cityLine, addr.country]
      .map((p) => (typeof p === 'string' ? p.trim() : ''))
      .filter(Boolean)
      .map((p) => this.#escape(p))
      .join('<br>');
  }

  /**
   * Flat, single-line address for the clipboard — pasteable straight into
   * Google Maps (or any maps app) by the customer.
   *
   * @param {Record<string, unknown>} addr
   * @returns {string}
   */
  #plainAddress(addr) {
    if (!addr || typeof addr !== 'object') return '';
    return [
      addr.addressLine1,
      addr.addressLine2,
      addr.addressLine3,
      addr.city,
      addr.state,
      addr.zip,
      addr.country,
    ]
      .map((p) => (typeof p === 'string' ? p.trim() : ''))
      .filter(Boolean)
      .join(', ');
  }

  /**
   * Copies an address to the clipboard and briefly confirms via the icon.
   * @param {Event} event
   */
  #onCopyClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const btn = /** @type {HTMLElement | null} */ (target?.closest('[data-copy-address]'));
    if (!btn) return;

    const text = btn.getAttribute('data-copy-address') || '';
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      btn.innerHTML = CHECK_ICON;
      btn.setAttribute('aria-label', 'Copied');
      clearTimeout(this.#copyTimer);
      this.#copyTimer = setTimeout(() => {
        btn.innerHTML = COPY_ICON;
        btn.setAttribute('aria-label', 'Copy address');
      }, 2000);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  /**
   * Store descriptions are rich HTML (banner `<img>`, `<br>`, links, lists,
   * formatting). Render them as-is inside the drawer's `.rte` container, but
   * sanitise first: parse into an inert document, drop anything executable or
   * unsafe, keep the formatting tags, constrain images, and make links open
   * safely.
   *
   * @param {unknown} raw
   * @returns {string} sanitised HTML
   */
  #sanitizeDescription(raw) {
    if (typeof raw !== 'string' || !raw.trim()) return '';

    // DOMParser('text/html') is inert — no scripts run and no resources load here.
    const doc = new DOMParser().parseFromString(raw, 'text/html');

    doc.body
      .querySelectorAll('script, iframe, object, embed, style, link, meta, base, form, input, textarea')
      .forEach((el) => el.remove());

    doc.body.querySelectorAll('*').forEach((el) => {
      for (const attr of [...el.attributes]) {
        const name = attr.name.toLowerCase();
        const value = attr.value.replace(/\s+/g, '').toLowerCase();
        if (name.startsWith('on') || ((name === 'href' || name === 'src') && value.startsWith('javascript:'))) {
          el.removeAttribute(attr.name);
        }
      }
      if (el.tagName === 'IMG') {
        el.style.maxWidth = '100%';
        el.style.height = 'auto';
        el.setAttribute('loading', 'lazy');
      }
      if (el.tagName === 'A' && el.getAttribute('href')) {
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noopener noreferrer');
      }
    });

    return doc.body.innerHTML;
  }

  /** @param {string} str */
  #escape(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /** @param {string} str */
  #escapeAttr(str) {
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /** Close handler for the declarative `on:click="store-walk-in-drawer/close"` binding. */
  close() {
    this.closeDialog();
  }
}

if (!customElements.get('store-walk-in-drawer')) {
  customElements.define('store-walk-in-drawer', StoreWalkInDrawer);
}
